import { useRef, useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'

import { Sentry } from './instrument'
import BackgroundMusic from './components/BackgroundMusic'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import PageTransition from './components/PageTransition'
import ProtectedRoute from './components/ProtectedRoute'
import TransparentLogo, { getTransparentLogoSrc } from './components/TransparentLogo'

// Eagerly loaded — shown immediately on first visit
import Home from './pages/Home'

// Lazily loaded — code-split per route
const History     = lazy(() => import('./pages/History'))
const Festivals   = lazy(() => import('./pages/Festivals'))
const Pooja       = lazy(() => import('./pages/Pooja'))
const Donations   = lazy(() => import('./pages/Donations'))
const Annadhanam  = lazy(() => import('./pages/Annadhanam'))
const Gallery     = lazy(() => import('./pages/Gallery'))
const Location    = lazy(() => import('./pages/Location'))
const Trustees    = lazy(() => import('./pages/Trustees'))
const AdminLogin      = lazy(() => import('./pages/Admin/Login'))
const AdminDashboard  = lazy(() => import('./pages/Admin/Dashboard'))
const AdminEvents     = lazy(() => import('./pages/Admin/Events'))
const AdminGallery    = lazy(() => import('./pages/Admin/GalleryAdmin'))
const AdminAnnadhanam = lazy(() => import('./pages/Admin/AnnadhanamAdmin'))
const AdminDonations  = lazy(() => import('./pages/Admin/DonationsAdmin'))
const AdminBookings   = lazy(() => import('./pages/Admin/Bookings'))

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="text-charcoal/30 text-sm">Loading...</div>
    </div>
  )
}

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

// Wrap Routes so Sentry tracks route changes as transactions
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true }

/* ─── Loading Screen ─── */
function LoadingScreen({ onDone }) {
  const ref = useRef(null)
  const [logoSrc, setLogoSrc] = useState('/galary/logo2.jpg')

  // Use the shared cached canvas processing (same result as TransparentLogo)
  useEffect(() => {
    getTransparentLogoSrc().then(setLogoSrc)
  }, [])

  useEffect(() => {
    // Timer is the authoritative exit trigger — GSAP is visual-only
    const timer = setTimeout(onDone, 3400)

    const ctx = gsap.context(() => {
      const tl = gsap.timeline()

      // ① Logo rises from far below into centre
      tl.fromTo('.ls-logo-wrap',
        { y: 340, opacity: 0, scale: 0.45 },
        { y: 0, opacity: 1, scale: 1, duration: 1.05, ease: 'power3.out' },
        0.15
      )
      // ② Divine settle: brief scale-up then spring back
      .to('.ls-logo-wrap',
        { scale: 1.1, duration: 0.18, ease: 'power2.out' }, 1.15
      )
      .to('.ls-logo-wrap',
        { scale: 1.0, duration: 0.3, ease: 'elastic.out(1, 0.45)' }, 1.33
      )
      // ③ Glow ring blooms out on settle
      .fromTo('.ls-glow-ring',
        { scale: 0.6, opacity: 0 },
        { scale: 1.25, opacity: 0.75, duration: 0.42, ease: 'power2.out' }, 1.1
      )
      .to('.ls-glow-ring',
        { scale: 1.05, opacity: 0.35, duration: 0.6, ease: 'power1.inOut' }, 1.52
      )
      // ④ First namam — rises in right after logo settles
      .fromTo('.ls-namam-1',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 1.3
      )
      // ⑤ Second namam
      .fromTo('.ls-namam-2',
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.48, ease: 'power2.out' }, 1.52
      )
      // ⑥ Temple name
      .fromTo('.ls-name',
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 1.72
      )
      // ⑦ Location tag
      .fromTo('.ls-location',
        { opacity: 0 },
        { opacity: 1, duration: 0.4 }, 1.92
      )
      // ⑧ Saffron wave rises from bottom after text is fully visible
      .to('.ls-fill',
        { scaleY: 1, duration: 0.85, ease: 'power2.inOut', transformOrigin: 'bottom' }, 2.1
      )
      // ⑨ Fade out entire screen
      .to(ref.current,
        { opacity: 0, duration: 0.4, ease: 'power2.in' }, 2.98
      )
    }, ref)

    return () => { clearTimeout(timer); ctx.revert() }
  }, [])

  return (
    <div
      ref={ref}
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#0D0500' }}
    >
      {/* Saffron wave — rises last */}
      <div
        className="ls-fill absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #FF6B00 0%, #E65100 100%)',
          transform: 'scaleY(0)',
          transformOrigin: 'bottom',
        }}
      />

      {/* Ambient radial glow rising from below — sets the divine mood */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          width: '700px',
          height: '340px',
          background: 'radial-gradient(ellipse at bottom center, rgba(255,107,0,0.22) 0%, rgba(255,183,77,0.08) 45%, transparent 70%)',
        }}
      />

      {/* Main content */}
      <div className="relative z-10 text-center px-6 flex flex-col items-center">

        {/* Logo + glow ring — animates from bottom */}
        <div className="ls-logo-wrap relative mb-6 flex items-center justify-center" style={{ width: 120, height: 120 }}>
          {/* Pulsing golden glow ring behind the logo */}
          <div
            className="ls-glow-ring absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(255,183,77,0.55) 0%, rgba(255,107,0,0.25) 45%, transparent 70%)',
              boxShadow: '0 0 48px 24px rgba(255,107,0,0.35), 0 0 90px 50px rgba(255,183,77,0.12)',
            }}
          />
          {/* Logo image — white background removed via canvas */}
          <img
            src={logoSrc}
            alt="Temple Logo"
            style={{
              width: 104,
              height: 104,
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 18px rgba(255,183,77,0.9)) drop-shadow(0 4px 24px rgba(255,107,0,0.6))',
              position: 'relative',
              zIndex: 1,
            }}
          />
        </div>

        {/* First sacred namam */}
        <p className="ls-namam-1 font-tamil text-gold text-2xl leading-relaxed mb-1">
          ஓம் நமோ நாராயணாய
        </p>
        {/* Second sacred namam */}
        <p className="ls-namam-2 font-tamil text-gold/80 text-base mb-5">
          ஸ்ரீ லட்சுமி நரசிம்ஹாய நமஹ
        </p>

        {/* Temple name */}
        <p className="ls-name font-display text-white text-xl font-bold leading-tight">
          ஸ்ரீ லட்சுமி நரசிம்ஹ பெருமாள் ஆலயம்
        </p>
        {/* Location */}
        <p className="ls-location text-white/50 text-sm mt-1 tracking-widest uppercase">
          கெங்கநல்லூர்
        </p>
      </div>
    </div>
  )
}

/* ─── Global Floating Buttons ─── */
function GlobalFloats() {
  const [showTop, setShowTop] = useState(false)
  const donateRef = useRef(null)

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!donateRef.current) return
    const anim = gsap.to(donateRef.current, {
      scale: 1.1, duration: 1.5, repeat: -1, yoyo: true, ease: 'power1.inOut',
    })
    return () => anim.kill()
  }, [])

  function scrollTop() {
    gsap.to(window, { scrollTo: 0, duration: 0.8, ease: 'power2.out' })
  }

  return (
    <>
      <div ref={donateRef} className="fixed bottom-24 right-6 z-40">
        <Link
          to="/donations"
          className="w-14 h-14 bg-saffron rounded-full flex items-center justify-center shadow-xl text-white text-2xl hover:bg-saffron-dark transition-colors"
          aria-label="Donate" title="Make a Donation"
        >
          🙏
        </Link>
      </div>
      {showTop && (
        <button
          onClick={scrollTop}
          className="fixed bottom-6 left-6 z-40 w-10 h-10 bg-charcoal text-white rounded-full flex items-center justify-center shadow-lg hover:bg-charcoal-soft transition-colors"
          aria-label="Scroll to top"
        >↑</button>
      )}
    </>
  )
}

function ScrollHandler() {
  const location = useLocation()

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1)
      const tryScroll = () => {
        const el = document.getElementById(id)
        if (el) {
          gsap.to(window, { scrollTo: { y: el, offsetY: 88 }, duration: 0.8, ease: 'power2.out' })
          return true
        }
        return false
      }
      // Try immediately (same-page hash click)
      if (!tryScroll()) {
        // New page + hash: wait for render then scroll
        const t = setTimeout(tryScroll, 350)
        return () => clearTimeout(t)
      }
    } else {
      window.scrollTo(0, 0)
    }
  }, [location])

  return null
}

function AppLayout() {
  const location = useLocation()
  const isAdmin = location.pathname.startsWith('/admin')
  const isHome  = location.pathname === '/'

  return (
    <>
      <ScrollHandler />
      {/* Audio only on the home/hero page — placed here (outside PageTransition)
          so position:fixed is never broken by a GSAP-transformed ancestor */}
      {isHome && <BackgroundMusic />}
      {!isAdmin && <Navbar />}
      <Suspense fallback={<PageLoader />}>
        <SentryRoutes>
          <Route path="/"           element={<PageTransition><Home /></PageTransition>} />
          <Route path="/history"    element={<PageTransition><History /></PageTransition>} />
          <Route path="/festivals"  element={<PageTransition><Festivals /></PageTransition>} />
          <Route path="/pooja"      element={<PageTransition><Pooja /></PageTransition>} />
          <Route path="/donations"  element={<PageTransition><Donations /></PageTransition>} />
          <Route path="/annadhanam" element={<PageTransition><Annadhanam /></PageTransition>} />
          <Route path="/gallery"    element={<PageTransition><Gallery /></PageTransition>} />
          <Route path="/location"   element={<PageTransition><Location /></PageTransition>} />
          <Route path="/trustees"   element={<PageTransition><Trustees /></PageTransition>} />
          <Route path="/admin/login"   element={<AdminLogin />} />
          <Route path="/admin"         element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/events"             element={<ProtectedRoute><AdminEvents /></ProtectedRoute>} />
          <Route path="/admin/gallery"            element={<ProtectedRoute><AdminGallery /></ProtectedRoute>} />
          <Route path="/admin/annadhanam-manage"  element={<ProtectedRoute><AdminAnnadhanam /></ProtectedRoute>} />
          <Route path="/admin/donations-manage"   element={<ProtectedRoute><AdminDonations /></ProtectedRoute>} />
          <Route path="/admin/bookings"           element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
      </Suspense>
      {!isAdmin && <Footer />}
      {!isAdmin && <GlobalFloats />}
    </>
  )
}

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cream text-center px-6">
      <TransparentLogo size={56} glow className="mb-6" />
      <h1 className="font-display text-4xl font-bold text-charcoal mb-2">404</h1>
      <p className="text-charcoal-soft mb-6">Page not found. The divine path you seek does not exist.</p>
      <a href="/" className="bg-saffron text-white font-semibold px-6 py-3 rounded-full hover:bg-saffron-dark transition-colors">
        Return Home
      </a>
    </div>
  )
}

function SentryFallback({ error }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cream text-center px-6">
      <TransparentLogo size={56} glow />
      <h1 className="font-display text-2xl font-bold text-charcoal mt-6 mb-2">Something went wrong</h1>
      <p className="text-charcoal-soft mb-6">We're sorry for the inconvenience. Please try refreshing the page.</p>
      <button
        onClick={() => window.location.reload()}
        className="bg-saffron text-white font-semibold px-6 py-3 rounded-full hover:bg-saffron-dark transition-colors"
      >
        Refresh Page
      </button>
    </div>
  )
}

export default function App() {
  const [loading, setLoading] = useState(true)

  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <BrowserRouter future={ROUTER_FUTURE}>
        {loading && <LoadingScreen onDone={() => setLoading(false)} />}
        {!loading && <AppLayout />}
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}
